import { useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { mutation, query } from '../api'
import {
  FLAGS_SYSTEM_ID,
  RESULTS_SYSTEM_ID,
  isConditionalCheckFailed,
  parseFlagsSystem,
  parseResultsSystem,
  systemWriteRequest
} from '../helper/experiments'
import { FUNNEL_FORM_SOURCE } from '../helper/funnelDropouts'

export const FLAGS_QUERY_KEY = ['system', FLAGS_SYSTEM_ID]
export const RESULTS_QUERY_KEY = ['system', RESULTS_SYSTEM_ID]

// Die Flags wurden seit dem Laden geändert (z. B. vom Evaluator). `fresh` ist
// der neu geladene Stand, auf dem der Aufrufer weiterarbeiten kann.
export class FlagsConflictError extends Error {
  constructor(fresh) {
    super('FEATURE_FLAGS wurde inzwischen geändert')
    this.name = 'FlagsConflictError'
    this.fresh = fresh
  }
}

export const CONFLICT_MESSAGE =
  'Die Flags wurden inzwischen geändert, z. B. vom täglichen Evaluator. Der aktuelle Stand ist neu geladen, bitte prüfe ihn und versuch es noch einmal.'

const fetchSystem = (id) => query({ query: 'getSystem', variables: { id } })

export const useFeatureFlags = () => {
  const queryClient = useQueryClient()

  // getSystem liefert null, solange das Item nicht existiert.
  const flagsQuery = useQuery({
    queryKey: FLAGS_QUERY_KEY,
    queryFn: () => fetchSystem(FLAGS_SYSTEM_ID),
    select: parseFlagsSystem
  })

  const resultsQuery = useQuery({
    queryKey: RESULTS_QUERY_KEY,
    queryFn: () => fetchSystem(RESULTS_SYSTEM_ID),
    select: parseResultsSystem
  })

  /**
   * Schreibt alle Flags. `base` ist der Stand, auf dem die Änderung beruht
   * (parseFlagsSystem). Bei einem Konflikt wird neu geladen und ein
   * FlagsConflictError mit dem frischen Stand geworfen.
   */
  const saveFlags = useCallback(
    async (base, nextFlags) => {
      const request = systemWriteRequest(base, nextFlags)
      try {
        const item = await mutation(request)
        queryClient.setQueryData(FLAGS_QUERY_KEY, item)
        return parseFlagsSystem(item)
      } catch (err) {
        if (!isConditionalCheckFailed(err)) throw err
        const freshItem = await fetchSystem(FLAGS_SYSTEM_ID)
        queryClient.setQueryData(FLAGS_QUERY_KEY, freshItem)
        throw new FlagsConflictError(parseFlagsSystem(freshItem))
      }
    },
    [queryClient]
  )

  return { flagsQuery, resultsQuery, saveFlags }
}

// Dieselbe Abfrage wie auf den Seiten Leads und Funnel-Abbrecher (gleicher
// Query-Key, also gemeinsamer Cache). Nur lesen.
export const useFunnelForms = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: ['funnel-forms'],
    queryFn: () =>
      query({
        query: 'listForms',
        variables: {
          filter: { form_source: { eq: FUNNEL_FORM_SOURCE } }
        }
      }),
    enabled
  })
